import {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router";
import GridElement from "../../Common/GridElement";
import {deleteChangePersonGroup, getChangePersonGroups} from "../../../API/changePersonGroup";
import {
    getUserSettings,
    PROCESSED
} from "../../Utils/constants";
import {IconButton, Tooltip} from "@mui/material";
import {Delete, Visibility} from "@mui/icons-material";
import {selectors as accountSelectors} from "../../../Ducks/account";
import {connect} from "react-redux";
import {toast} from "react-toastify";
import Swal from "sweetalert2";
import {showEllipsis} from "../../Utils/stringUtils";
import * as React from "react";
import {DateRangeColumnFilter} from "../../Common/GridFilters/DateRangeColumnFilter";
import {DefaultColumnFilter} from "../../Common/GridFilters/DefaultColumnFilter";
import {makeAccessor, makeHeader, prepareFilterQuery, prepareSortQuery} from "../../Utils/gridUtils";
import {updateUserSetting} from "../../../API/userSettings";

const ChangePersonGroupGrid = (props) => {
    const {navigate, canReviewGroupChange} = props;
    let {user, userMeta} = getUserSettings();
    const lastSortBy = user?.tableSettings?.person_group_changes?.sortBy ? user.tableSettings.person_group_changes.sortBy : null;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [rowsCount, setRowsCount] = useState(0);
    const [filters, setFilters] = useState(() => {
        return user?.tableSettings?.person_group_changes?.filters ? user.tableSettings.person_group_changes.filters : [];
    });
    const [controlledPageIndex, setControlledPageIndex] = useState(0);
    const [refreshGridData, setRefreshGridData] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [cols, setCols] = useState([]);

    useEffect(() => {
        if (userMeta.table_columns.hasOwnProperty("person_group_changes")) {
            let columns =  userMeta.table_columns.person_group_changes;
            let visibleColumns = user.tableSettings.hasOwnProperty("person_group_changes")
                    && user.tableSettings.person_group_changes?.columns ? user.tableSettings.person_group_changes?.columns : [];
            let invisibleColumns = columns.filter((column) => !visibleColumns.includes(column));
            columns = [...visibleColumns, ...invisibleColumns];

            setCols(getVisibleColumns(columns, visibleColumns));
        }
    }, []);

    const getVisibleColumns = (columns, visibleColumns) => {
        const dynamicColumns = columns?.map(key =>
            {
                let visible = visibleColumns.includes(key);

                if (visibleColumns?.length === 0) {
                    visible = true;
                }

                let col = {
                    column: key,
                    accessor: makeAccessor(key),
                    minWidth: 150,
                    Header: makeHeader(key),
                    filter: (isDateColumn(key)) ? 'dateRange' : 'fuzzyText',
                    Filter: (isDateColumn(key)) ? DateRangeColumnFilter: DefaultColumnFilter,
                    visible: visible,
                    formatDate: isDateColumn(key)
                }

                if (key === "purchases.purchase_id") {
                    col["Cell"] = ({value, row}) => (
                        <a href={`${window.location.origin}/sales/id/${row.original.purchase_id}`} target='blank'>
                            {value}
                        </a>
                    );
                }

                if (key === "person_name") {
                    col["Cell"] = ({value, row}) => (
                        <a href={`${window.location.origin}/contact/id/${row.original.person_id}`} target='blank'>
                            {value}
                        </a>
                    );
                }

                if (key === "comments") {
                    col["Cell"] = ({row}) => (
                        <div>
                            {
                                row.original.comments?.length >= 50 ? showEllipsis(row.original.comments, 50) : row.original.comments
                            }
                        </div>
                    );
                }

                return col;
            }
        );

        dynamicColumns?.push({
            Header: "Action",
            filter: "fuzzyText",
            Cell: ({row}) => (
                <div>
                    {row.original.status !== PROCESSED && canReviewGroupChange && (
                        <Tooltip title='Review memeber group change request' arrow placement='top'>
                            <IconButton
                                aria-label='process'
                                onClick={() => navigate("/change-member-group/id/" + row.original.person_group_change_id)}
                                disabled={isDeleting}
                            >
                                <Visibility color='primary' disabled={isDeleting}/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {
                        row.original.status !== PROCESSED && canReviewGroupChange && (
                            <Tooltip title='Delete Request' arrow placement='top'>
                                <IconButton
                                    aria-label='delete'
                                    aria-haspopup='true'
                                    onClick={() => handleDeleteGroupChangeClick(row.original.person_group_change_id)}
                                    color="error"
                                    disabled={isDeleting}>
                                    <Delete disabled={isDeleting}/>
                                </IconButton>
                            </Tooltip>
                        )
                    }
                </div>
            )
        });

        return dynamicColumns ? [...dynamicColumns] : [];
   }

    const isDateColumn = (key) => {
        return (key === "request_time"
            || key === "first_meeting_date")
    }

    const fetchData = useCallback(({pageSize, pageIndex, sortBy, filters, columns}) => {
        //fetchData will be called whenever the table needs new data.
        let _columns = columns.filter((item) => item.visible).map((item) => item.column);
        _columns = _columns.filter((item) => item !== undefined);
        _columns.push('persons.person_id');

        setLoading(true);

        // Check filter data
        const query = prepareFilterQuery(filters, columns);
        const sort = prepareSortQuery(sortBy, columns);

        getChangePersonGroups(pageSize, _columns, pageIndex, query, sort)
            .then((res) => {
                setData(res.data.main);
                setPageCount(res.data.offset.highest_offset);
                setRowsCount(res.data.offset.rows_count);
                setLoading(false);
                updateUserSetting(getUserSettings()['user']).then(() => {});
            })
            .catch(() => {
                setLoading(false);
            });

        setRefreshGridData(false); //if grid fetches the latest data then set refreshGridData to false
    }, []);

    const resetFilters = useCallback(() => setFilters([]), [setFilters]);

    const handleDeleteGroupChangeClick = (changePersonGroupId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsDeleting(true);
                deleteChangePersonGroup(changePersonGroupId).then((res) => {
                    toast.success(res.message);
                    setRefreshGridData(true);
                    setIsDeleting(false);
                }).catch((error) => {
                    toast.error(error);
                    setIsDeleting(false);
                });
            }
        });
    }

    return (
        <>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <h2 className='page-title'>Member Changes Group</h2>
            </div>

            <GridElement
                columns={cols}
                data={data}
                fetchData={fetchData}
                loading={loading}
                pageCount={pageCount}
                controlledPageIndex={controlledPageIndex}
                setControlledPageIndex={setControlledPageIndex}
                filters={filters}
                setFilters={setFilters}
                resetFilters={resetFilters}
                rowsCount={rowsCount}
                handleRowClick={() => {
                }}
                initialSortBy='request_time'
                sortOrder='desc'
                hideSelection={true}
                refreshGridData={refreshGridData}
                showColumnsControl={true}
                showCsvDownloadControl={true}
                setColumns={setCols}
                table="person_group_changes"
                lastSortBy={lastSortBy}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    canReviewGroupChange: accountSelectors.canReviewGroupChange(state)
});

function WithNavigate(props) {
    let navigate = useNavigate();
    return <ChangePersonGroupGrid {...props} navigate={navigate}/>;
}

export default connect(mapStateToProps)(WithNavigate);